import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_SETTINGS,
  fetchRemoteSettings,
  loadLocalSettings,
  saveLocalSettings,
  upsertRemoteSettings,
  type UserSettings,
} from '@/lib/queries/settings';
import { useSession } from './useSession';

/**
 * Hybrid settings hook.
 *
 *  - Anonymous: localStorage round-trip.
 *  - Signed in: Supabase is truth; localStorage is a paint cache.
 *  - First-time sign-in: if Supabase has no row for this user, we upsert
 *    the local settings (one-time migration). If it has one, server wins
 *    and overwrites the local cache.
 *
 * Component-facing API stays `{ settings, update, status }` — unchanged
 * from Phase 1 callers.
 */
export function useSettings() {
  const session = useSession();
  const userId = session.data?.user.id ?? null;
  const queryClient = useQueryClient();

  // 1. Paint immediately from local cache.
  const [local, setLocal] = useState<UserSettings>(() => loadLocalSettings());

  // 2. If signed in, fetch remote.
  const remote = useQuery<UserSettings | null, Error>({
    queryKey: ['user_settings', userId],
    queryFn: () => (userId ? fetchRemoteSettings(userId) : Promise.resolve(null)),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });

  // 3. Migrate / adopt remote once it resolves.
  const migratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!userId) {
      migratedFor.current = null;
      return;
    }
    if (remote.isLoading || remote.isError) return;
    if (migratedFor.current === userId) return;

    // `remote.data` is `UserSettings | null | undefined`. The `=== null`
    // check below only narrows out null, so we capture in a const and
    // branch on the three states explicitly. `undefined` means the query
    // hasn't actually returned yet (race against isLoading flipping);
    // we skip and the effect will fire again when data lands.
    const remoteSettings = remote.data;

    if (remoteSettings === null) {
      // First sign-in on this user: push local → remote.
      void upsertRemoteSettings(userId, local).then(() => {
        queryClient.setQueryData(['user_settings', userId], local);
      });
      migratedFor.current = userId;
    } else if (remoteSettings) {
      // Returning user: adopt remote and keep local cache in sync.
      setLocal(remoteSettings);
      saveLocalSettings(remoteSettings);
      migratedFor.current = userId;
    }
    // We intentionally exclude `local` from deps — we read it once at
    // migration time. Including it would loop the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, remote.data, remote.isLoading, remote.isError]);

  // 4. Updates fan out to both surfaces.
  const update = useCallback(
    (patch: Partial<UserSettings>) => {
      setLocal((prev) => {
        const next = { ...prev, ...patch };
        saveLocalSettings(next);
        if (userId) {
          void upsertRemoteSettings(userId, next)
            .then(() => {
              queryClient.setQueryData(['user_settings', userId], next);
            })
            .catch(() => {
              // Network failure: the local cache still reflects the user's
              // intent; we'll re-sync on the next mount.
            });
        }
        return next;
      });
    },
    [userId, queryClient],
  );

  return {
    settings: local,
    update,
    /** `'local'` while anonymous, `'syncing'` during first remote load, `'remote'` once reconciled. */
    status: !userId
      ? ('local' as const)
      : remote.isLoading || migratedFor.current !== userId
        ? ('syncing' as const)
        : ('remote' as const),
  };
}

/** Re-export for components that only need the type. */
export type { UserSettings };

/** Re-export for components that want to spell out defaults. */
export { DEFAULT_SETTINGS };
